import {
	Clear,
	Search,
	AddOutlined as AddOutlinedIcon
} from "@mui/icons-material";

import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import {
	format,
	startOfMonth,
	endOfMonth,
	subDays,
	subMonths,
	subYears,
	addMonths,
	parseISO
} from "date-fns";

import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";

export type PoliceClientOption = {
	id: string;
	label: string;
};

interface PoliceToolbarProps {
	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	onClearTextSearch?: () => void;

	showInputSearch?: boolean;

	showDateRange?: boolean;
	onChangeDateStart?: (value: string) => void;
	onChangeDateEnd?: (value: string) => void;

	showStatusSelect?: boolean;
	onChangeStatus?: (value: string) => void;

	showClientSelect?: boolean;            // 🔥 NOVO
	onChangeClient?: (value: string | null) => void; // 🔥 NOVO

	showNewPoliceButton?: boolean;
	onClickNewPolice?: () => void;

	showSearchButton?: boolean;
	onClickSearch?: () => void;

	showRefreshButton?: boolean;
	onClickRefresh?: () => void;
}

export function PoliceToolbar({
	textSearch = "",
	onChangeTextSearch,
	onClearTextSearch,

	showInputSearch = true,

	showDateRange = true,
	onChangeDateStart,
	onChangeDateEnd,

	showStatusSelect = false,
	onChangeStatus,

	showClientSelect = false,       // 🔥 NOVO
	onChangeClient,                 // 🔥 NOVO

	showSearchButton,
	onClickSearch,

	showRefreshButton = true,
	onClickRefresh,

	showNewPoliceButton = true,
	onClickNewPolice,
}: PoliceToolbarProps) {

	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
	const { debounce } = useDebounce();

	// --------------------------
	// Datas & período
	// --------------------------
	const today = new Date();
	const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
	const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
	const [periodType, setPeriodType] = useState("1_mes");

	const [status, setStatus] = useState("todos");

	// --------------------------
	// 🔥 Autocomplete de cliente (igual no Quote)
	// --------------------------
	const [clientInput, setClientInput] = useState("");
	const [clientSelected, setClientSelected] = useState<PoliceClientOption | null>(null);
	const [clientLoading, setClientLoading] = useState(false);
	const [clientOptions, setClientOptions] = useState<PoliceClientOption[]>([]);

	useEffect(() => {
		if (!showClientSelect) return;
		if (!clientInput) {
			setClientOptions([]);
			return;
		}

		setClientLoading(true);

		debounce(() => {
			clientService
				.getSelect(1, clientInput.trim())
				.then((result: any) => {
					if (result instanceof Error) return;

					const list = result?.data?.map((c: any) => ({
						id: c.id,
						label: c.name,
					})) ?? [];

					setClientOptions(list);
				})
				.finally(() => setClientLoading(false));
		});

	}, [clientInput, showClientSelect]);

	const handleSelectClient = (v: PoliceClientOption | null) => {
		setClientSelected(v);
		onChangeClient?.(v?.id ?? null);
	};

	// --------------------------
	// Períodos
	// --------------------------
	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start = now;
		let end = now;

		switch (period) {
			case "15_dias": start = subDays(now, 15); break;
			case "1_mes": start = startOfMonth(now); end = endOfMonth(now); break;
			case "2_meses": start = startOfMonth(subMonths(now, 1)); end = endOfMonth(now); break;
			case "3_meses": start = startOfMonth(subMonths(now, 2)); end = endOfMonth(now); break;
			case "1_ano": start = startOfMonth(subYears(now, 1)); end = endOfMonth(now); break;
			case "mes_anterior": start = startOfMonth(subMonths(now, 1)); end = endOfMonth(subMonths(now, 1)); break;
			case "proximo_mes": start = startOfMonth(addMonths(now, 1)); end = endOfMonth(addMonths(now, 1)); break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [d1, d2] = getDateRangeFromPeriod(value);
			setStartDate(d1);
			setEndDate(d2);
			onChangeDateStart?.(d1);
			onChangeDateEnd?.(d2);
		}
	};

	const handleSearchClick = () => {
		onClickSearch?.();
		onClickRefresh?.();
	};

	const effectiveShowSearchButton =
		typeof showSearchButton === "boolean" ? showSearchButton : showRefreshButton;

	return (
		<Box component={Paper} padding={2} mx={1} display="flex" flexDirection="column" gap={2}>

			{/* LINHA SUPERIOR */}
			<Grid container spacing={2} alignItems="center">

				{/* CLIENTE (IMOBILIÁRIA) */}
				{showClientSelect && (
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							fullWidth
							size="small"
							loading={clientLoading}
							options={clientOptions}
							value={clientSelected}
							onChange={(_, v) => handleSelectClient(v)}
							onInputChange={(_, value) => setClientInput(value)}
							renderInput={(params) => (
								<TextField {...params} label="Imobiliária" />
							)}
						/>
					</Grid>
				)}

				{/* PERÍODO */}
				{showDateRange && (
					<Grid item xs={12} sm={4} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Período</InputLabel>
							<Select
								label="Período"
								value={periodType}
								onChange={handlePeriodChange}
							>
								<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
								<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
								<MenuItem value="1_mes">Mês Atual</MenuItem>
								<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
								<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
								<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
								<MenuItem value="1_ano">Último ano</MenuItem>
								<MenuItem value="personalizado">Personalizado</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{/* DATA PERSONALIZADA */}
				{showDateRange && periodType === "personalizado" && (
					<>
						<Grid item xs={12} sm={4} md={2}>
							<TextField
								fullWidth
								type="date"
								size="small"
								label="Data Inicial"
								value={startDate}
								onChange={(e) => {
									setStartDate(e.target.value);
									onChangeDateStart?.(e.target.value);
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={4} md={2}>
							<TextField
								fullWidth
								type="date"
								size="small"
								label="Data Final"
								value={endDate}
								onChange={(e) => {
									setEndDate(e.target.value);
									onChangeDateEnd?.(e.target.value);
								}}
							/>
						</Grid>
					</>
				)}

				{/* STATUS */}
				{showStatusSelect && (
					<Grid item xs={12} sm={4} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Status</InputLabel>
							<Select
								label="Status"
								value={status}
								onChange={(e) => {
									setStatus(e.target.value);
									onChangeStatus?.(e.target.value);
								}}
							>
								<MenuItem value="todos">Todos</MenuItem>
								{/* <MenuItem value="pending">Pendente</MenuItem> */}
								<MenuItem value="open">Vigente</MenuItem>
								<MenuItem value="expired">Expirado</MenuItem>
								<MenuItem value="canceled">Cancelado</MenuItem>
								<MenuItem value="canceling">Em Cancelamento</MenuItem>
								<MenuItem value="to_invoice">Apolice a faturar</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}
			</Grid>

			{/* LINHA DE PESQUISA */}
			<Grid container spacing={2} alignItems="center">

				{/* SEARCH */}
				{showInputSearch && (
					<Grid item xs={12} sm={8} md={4}>
						<TextField
							fullWidth
							size="small"
							placeholder="Pesquisar por nome, documento ou apólice..."
							value={textSearch}
							onChange={(e) => onChangeTextSearch?.(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												onClearTextSearch?.();
												onChangeTextSearch?.("");
											}}
										>
											<Clear />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				)}

				{/* BOTÃO PESQUISAR */}
				{effectiveShowSearchButton && (
					<Grid item xs={12} sm={4} md="auto">
						<Button
							variant="contained"
							startIcon={<Search />}
							onClick={handleSearchClick}
							fullWidth={isSmall}
						>
							Pesquisar
						</Button>
					</Grid>
				)}

				{/* NOVA APÓLICE */}
				{showNewPoliceButton && (
					<Grid item xs={12} sm="auto" md sx={{ textAlign: "right" }}>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddOutlinedIcon />}
							onClick={onClickNewPolice}
							fullWidth={isSmall}
						>
							Nova Apólice
						</Button>
					</Grid>
				)}
			</Grid>

			{/* RESUMO PERÍODO */}
			{showDateRange && (
				<Typography
					variant="body2"
					sx={{ ml: 1, fontStyle: "italic", color: "text.secondary" }}
				>
					Período: {format(parseISO(startDate), "dd/MM/yyyy")} -{" "}
					{format(parseISO(endDate), "dd/MM/yyyy")}
				</Typography>
			)}

		</Box>
	);
}
